import React, { useMemo, useState, useCallback } from 'react';
import AddJobModal from './AddJobModal';
import { formatDate, displayDate, weekday as weekdayOf } from '../utils/dates';
import { IconButton, WideSection } from './ui';

function formatDayHeader(dateStr) {
  const weekday = formatDate(dateStr, { weekday: 'short' });
  const [, month, day] = dateStr.split('-').map(Number);
  return { weekday, date: `${month}/${day}` };
}

function isWeekend(dateStr) {
  const day = weekdayOf(dateStr);
  return day === 0 || day === 6;
}

function expectedHours(dateStr) {
  return isWeekend(dateStr) ? 0 : 8;
}

function formatRate(rate) {
  const num = parseFloat(rate);
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Compare two job-task rows by task line number (JobTaskID), numerically when
// possible, falling back to string order for non-numeric task numbers.
function taskNumCompare(a, b) {
  const na = parseInt(a.jobTaskId, 10);
  const nb = parseInt(b.jobTaskId, 10);
  const aNum = !isNaN(na);
  const bNum = !isNaN(nb);
  if (aNum && bNum && na !== nb) return na - nb;
  if (aNum !== bNum) return aNum ? -1 : 1;
  return String(a.jobTaskId || '').localeCompare(String(b.jobTaskId || ''));
}
export default function TabularSummary({ entries, dates, favorites, customers, rateCodes, defaultRateCodeId, holidays, onCellClick, onCellEdit, isSubmitted, isApproved, hasOverLimitDay, onSubmit, onReopen, onDeleteJob, onMoveEntries, onAddFavorite, onReorderJobs }) {
  // Holiday jobs can only be charged on holidays: their other days show "N/A".
  const holidayJobs = useMemo(() => new Set((holidays && holidays.jobs) || []), [holidays]);
  const isHolidayNA = useCallback(
    (jobId, date) => holidayJobs.has(jobId) && !Object.prototype.hasOwnProperty.call((holidays && holidays.dates) || {}, date),
    [holidayJobs, holidays]);

  const { jobs, groups, byDateJob, descByDateJob, entriesByDateJob, dateTotals, jobTotals, grandTotal } = useMemo(() => {
    const jobMap = new Map();
    const byDateJob = {};
    const descByDateJob = {};
    const entriesByDateJob = {};

    for (const entry of entries) {
      const date = entry.EntryDate?.split('T')[0];
      const jobKey = `${entry.JobID}|${entry.JobTaskID}`;
      const hrs = parseFloat(entry.Hours) || 0;

      if (!jobMap.has(jobKey)) {
        jobMap.set(jobKey, {
          key: jobKey,
          jobId: entry.JobID,
          jobName: entry.JobName,
          taskCode: entry.TaskCode,
          taskName: entry.TaskName,
          customerName: entry.CustomerName,
          jobTaskId: entry.JobTaskID,
          rateCodeId: entry.RateCodeID || null,
          rateCodeName: entry.RateCodeName || null,
          rate: entry.Rate != null ? entry.Rate : null,
        });
      } else {
        // Capture rate info from the first entry that has it
        const j = jobMap.get(jobKey);
        if (!j.rateCodeId && entry.RateCodeID) {
          j.rateCodeId = entry.RateCodeID;
          j.rateCodeName = entry.RateCodeName || null;
          j.rate = entry.Rate != null ? entry.Rate : null;
        }
      }

      if (!byDateJob[date]) byDateJob[date] = {};
      byDateJob[date][jobKey] = (byDateJob[date][jobKey] || 0) + hrs;

      if (!entriesByDateJob[date]) entriesByDateJob[date] = {};
      if (!entriesByDateJob[date][jobKey]) entriesByDateJob[date][jobKey] = [];
      entriesByDateJob[date][jobKey].push(entry);

      if (entry.Description) {
        if (!descByDateJob[date]) descByDateJob[date] = {};
        if (!descByDateJob[date][jobKey]) descByDateJob[date][jobKey] = [];
        descByDateJob[date][jobKey].push(entry.Description);
      }
    }

    // Also include favorites that may not have entries yet
    for (const fav of favorites) {
      const jobKey = `${fav.JobID}|${fav.JobTaskID}`;
      if (!jobMap.has(jobKey)) {
        jobMap.set(jobKey, {
          key: jobKey,
          jobId: fav.JobID,
          jobName: fav.JobName,
          taskCode: fav.TaskCode,
          taskName: fav.TaskName,
          customerName: fav.CustomerName,
          jobTaskId: fav.JobTaskID,
        });
      }
    }

    const jobs = Array.from(jobMap.values());

    // Sort jobs by favorites SortOrder (persisted user preference)
    const favOrder = new Map();
    for (const fav of favorites) {
      favOrder.set(`${fav.JobID}|${fav.JobTaskID}`, fav.SortOrder != null ? fav.SortOrder : Infinity);
    }
    jobs.sort((a, b) => {
      const sa = favOrder.get(a.key) ?? Infinity;
      const sb = favOrder.get(b.key) ?? Infinity;
      if (sa !== sb) return sa - sb;
      return (a.jobName || '').localeCompare(b.jobName || '');
    });

    const dateTotals = {};
    for (const date of dates) {
      dateTotals[date] = Object.values(byDateJob[date] || {}).reduce((s, h) => s + h, 0);
    }

    const jobTotals = {};
    for (const job of jobs) {
      jobTotals[job.key] = 0;
      for (const date of dates) {
        jobTotals[job.key] += (byDateJob[date]?.[job.key] || 0);
      }
    }

    const grandTotal = Object.values(jobTotals).reduce((s, h) => s + h, 0);

    // Group the job-task rows by job. Groups appear in the order the jobs first
    // occur in the (favorite-ordered) list; task lines within a group are sorted
    // by task line number. The job name is rendered once per group.
    const groupMap = new Map();
    for (const job of jobs) {
      if (!groupMap.has(job.jobId)) {
        groupMap.set(job.jobId, {
          jobId: job.jobId,
          jobName: job.jobName,
          customerName: job.customerName,
          tasks: [],
        });
      }
      groupMap.get(job.jobId).tasks.push(job);
    }
    const groups = Array.from(groupMap.values());
    for (const g of groups) g.tasks.sort(taskNumCompare);

    return { jobs, groups, byDateJob, descByDateJob, entriesByDateJob, dateTotals, jobTotals, grandTotal };
  }, [entries, dates, favorites]);

  const totalExpected = dates.reduce((s, d) => s + expectedHours(d), 0);
  const totalVariance = grandTotal - totalExpected;
  const isReadOnly = isSubmitted || isApproved;
  // Look up the user's default rate code name (defaultRateCodeId is a RateCodeID)
  const defaultRateCodeName = useMemo(() => {
    if (!defaultRateCodeId) return null;
    const match = (rateCodes || []).find((rc) => rc.RateCodeID === defaultRateCodeId);
    return match?.RateCodeName || defaultRateCodeId;
  }, [rateCodes, defaultRateCodeId]);

  const [showAddJob, setShowAddJob] = useState(false);

  // Drag and drop state – cell-level (individual date cells)
  const [dragSource, setDragSource] = useState(null); // { jobKey, date }
  const [dragOver, setDragOver] = useState(null); // { jobKey, date } of drop target cell

  const handleCellDragStart = useCallback((e, jobKey, date) => {
    if (isReadOnly) return;
    setDragSource({ jobKey, date });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ jobKey, date }));
    e.currentTarget.style.opacity = '0.4';
  }, [isReadOnly]);

  const handleCellDragEnd = useCallback((e) => {
    e.currentTarget.style.opacity = '1';
    setDragSource(null);
    setDragOver(null);
  }, []);

  const handleCellDragOver = useCallback((e, jobKey, date) => {
    e.preventDefault();
    if (!dragSource) return;
    // Allow drop on any cell that isn't the source cell
    if (jobKey === dragSource.jobKey && date === dragSource.date) return;
    e.dataTransfer.dropEffect = 'move';
    setDragOver({ jobKey, date });
  }, [dragSource]);

  const handleCellDragLeave = useCallback(() => {
    setDragOver(null);
  }, []);

  const handleCellDrop = useCallback((e, targetJobKey, targetDate) => {
    e.preventDefault();
    setDragOver(null);

    if (!dragSource || !onMoveEntries) {
      setDragSource(null);
      return;
    }

    // No-op if dropping on the same cell
    if (dragSource.jobKey === targetJobKey && dragSource.date === targetDate) {
      setDragSource(null);
      return;
    }

    const sourceJob = jobs.find((j) => j.key === dragSource.jobKey);
    const targetJob = jobs.find((j) => j.key === targetJobKey);
    if (!sourceJob || !targetJob) { setDragSource(null); return; }

    const sourceDate = dragSource.date;
    const sourceHours = byDateJob[sourceDate]?.[dragSource.jobKey] || 0;
    if (sourceHours === 0) { setDragSource(null); return; }

    // Block drop on a holiday job's non-holiday day
    if (isHolidayNA(targetJob.jobId, targetDate)) {
      setDragSource(null);
      alert('Holiday time can only be charged on a company holiday.');
      return;
    }

    // Block drop on cells that already have non-zero hours
    const targetHours = byDateJob[targetDate]?.[targetJobKey] || 0;
    if (targetHours > 0) {
      setDragSource(null);
      alert('Cannot move time to a job/job task/date combination that already has an entry.');
      return;
    }

    const { weekday: srcDay, date: srcShort } = formatDayHeader(sourceDate);
    const { weekday: tgtDay, date: tgtShort } = formatDayHeader(targetDate);
    const dateChanged = sourceDate !== targetDate;
    const jobChanged = dragSource.jobKey !== targetJobKey;

    let msg = `Move ${sourceHours.toFixed(2)} hours`;
    if (dateChanged && jobChanged) {
      msg += ` from ${srcDay} ${srcShort} to ${tgtDay} ${tgtShort}\nand from`;
    } else if (dateChanged) {
      msg += ` from ${srcDay} ${srcShort} to ${tgtDay} ${tgtShort} on`;
    } else {
      msg += ` on ${srcDay} ${srcShort} from`;
    }
    if (jobChanged) {
      msg += `\n"${sourceJob.jobName} (${sourceJob.jobId}) / ${sourceJob.jobTaskId} – ${sourceJob.taskCode}"\nto\n"${targetJob.jobName} (${targetJob.jobId}) / ${targetJob.jobTaskId} – ${targetJob.taskCode}"`;
    } else {
      msg += `\n"${sourceJob.jobName} (${sourceJob.jobId}) / ${sourceJob.jobTaskId} – ${sourceJob.taskCode}"`;
    }
    msg += '?\n\nHours and description will be moved.';
    if (jobChanged) msg += " The recipient job's rate code will be kept.";

    setDragSource(null);
    if (!window.confirm(msg)) return;

    onMoveEntries(sourceJob.jobId, sourceJob.jobTaskId, targetJob.jobId, targetJob.jobTaskId, sourceDate, dateChanged ? targetDate : undefined);
  }, [dragSource, jobs, byDateJob, onMoveEntries, isHolidayNA]);

  // Group (job) drag-and-drop state — reorder whole jobs (with their task lines
  // underneath), not individual task lines.
  const [groupDragIdx, setGroupDragIdx] = useState(null);
  const [groupDragOverIdx, setGroupDragOverIdx] = useState(null);

  const handleGroupDragStart = useCallback((e, gIdx) => {
    if (isReadOnly) return;
    setGroupDragIdx(gIdx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `group:${gIdx}`);
  }, [isReadOnly]);

  const handleGroupDragEnd = useCallback(() => {
    setGroupDragIdx(null);
    setGroupDragOverIdx(null);
  }, []);

  const handleGroupDragOver = useCallback((e, gIdx) => {
    e.preventDefault();
    if (groupDragIdx == null || groupDragIdx === gIdx) return;
    e.dataTransfer.dropEffect = 'move';
    setGroupDragOverIdx(gIdx);
  }, [groupDragIdx]);

  const handleGroupDragLeave = useCallback(() => {
    setGroupDragOverIdx(null);
  }, []);

  const handleGroupDrop = useCallback((e, targetGIdx) => {
    e.preventDefault();
    setGroupDragOverIdx(null);

    if (groupDragIdx == null || groupDragIdx === targetGIdx || !onReorderJobs) {
      setGroupDragIdx(null);
      return;
    }

    const newGroups = [...groups];
    const [moved] = newGroups.splice(groupDragIdx, 1);
    newGroups.splice(targetGIdx, 0, moved);

    const favMap = {};
    for (const fav of favorites) {
      favMap[`${fav.JobID}|${fav.JobTaskID}`] = fav.UserFavoriteID;
    }

    // Persist a flat order that keeps each job's task lines together and in task
    // line number order.
    const orderedIds = [];
    for (const g of newGroups) {
      for (const t of g.tasks) {
        const id = favMap[t.key];
        if (id != null) orderedIds.push(id);
      }
    }

    onReorderJobs(orderedIds);
    setGroupDragIdx(null);
  }, [groupDragIdx, groups, favorites, onReorderJobs]);

  // One-time sort of the job groups by job name or job number. This persists the
  // new order through onReorderJobs exactly like a manual drag — it does NOT keep
  // auto-sorting, so the user can still drag afterward and the order sticks.
  const sortGroupsBy = useCallback((key) => {
    if (!onReorderJobs) return;
    const sorted = [...groups].sort((a, b) => {
      if (key === 'jobId') {
        // Natural (numeric-aware) order on the job number, e.g. J2 before J16470.
        return String(a.jobId || '').localeCompare(String(b.jobId || ''), undefined, { numeric: true, sensitivity: 'base' });
      }
      return String(a.jobName || '').localeCompare(String(b.jobName || ''), undefined, { sensitivity: 'base' });
    });
    const favMap = {};
    for (const fav of favorites) favMap[`${fav.JobID}|${fav.JobTaskID}`] = fav.UserFavoriteID;
    const orderedIds = [];
    for (const g of sorted) {
      for (const t of g.tasks) {
        const id = favMap[t.key];
        if (id != null) orderedIds.push(id);
      }
    }
    onReorderJobs(orderedIds);
  }, [groups, favorites, onReorderJobs]);

  // Edit popup state
  const [editPopup, setEditPopup] = useState(null);
  const [editHours, setEditHours] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const handleCellRightClick = (e, job, date) => {
    e.preventDefault();
    if (isReadOnly || !onCellEdit) return;
    if (isHolidayNA(job.jobId, date) && !(byDateJob[date]?.[job.key] > 0)) return;
    const cellEntries = entriesByDateJob[date]?.[job.key] || [];
    const entry = cellEntries[0] || null;
    const hrs = byDateJob[date]?.[job.key] || 0;
    setEditPopup({ job, date, entry, hrs });
    setEditHours(hrs > 0 ? hrs.toString() : '');
    setEditDesc(entry?.Description || '');
  };

  const handleEditSave = async () => {
    if (!editPopup || !onCellEdit || editSaving) return;
    const hours = parseFloat(editHours);
    const origHours = editPopup.hrs;
    const origDesc = editPopup.entry?.Description || '';
    const noChange = (editHours === '' && origHours === 0 && editDesc === origDesc)
      || (hours === origHours && editDesc === origDesc);
    if (noChange) {
      setEditPopup(null);
      return;
    }
    if (editHours === '' || isNaN(hours)) {
      setEditPopup(null);
      return;
    }
    if (hours < 0 || hours > 24) {
      alert('Hours must be between 0 and 24');
      return;
    }
    if ((hours * 4) % 1 !== 0) {
      alert('Hours must be in 0.25 increments');
      return;
    }
    setEditSaving(true);
    try {
      await onCellEdit({
        jobId: editPopup.job.jobId,
        jobTaskId: editPopup.job.jobTaskId,
        date: editPopup.date,
        entry: editPopup.entry,
        hours,
        description: editDesc,
      });
      setEditPopup(null);
    } catch (e) {
      alert(e.message || 'Failed to save');
    }
    setEditSaving(false);
  };

  // Up to the full screen width on larger screens (margin-based, so fixed-position
  // pop-ups inside still cover the viewport).
  return (
    <WideSection fill maxWidth={1400}>
    <div style={{ padding: '12px 0 20px' }}>
      {/* Submit / Reopen */}
      {!isApproved && (
        <div className="action-row">
          {isSubmitted ? (
            <button className="btn btn-outline" onClick={onReopen}>Reopen Timesheet</button>
          ) : (
            <button className="btn btn-success" onClick={onSubmit} disabled={hasOverLimitDay}>Submit Timesheet</button>
          )}
        </div>
      )}
      {isApproved && (
        <div style={{ textAlign: 'center', color: '#059669', fontSize: '13px', padding: '8px 16px', fontWeight: 600 }}>
          Timesheet Approved
        </div>
      )}
      {hasOverLimitDay && !isSubmitted && !isApproved && (
        <div style={{ textAlign: 'center', color: '#dc2626', fontSize: '13px', padding: '0 16px 8px' }}>
          Cannot submit: one or more days exceed 24 hours.
        </div>
      )}

      {!isReadOnly && (
        <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--gray-400)', padding: '0 16px 6px' }}>
          Drag a day cell to move time &middot; Drag a job name to reorder jobs
        </div>
      )}

      {!isReadOnly && onReorderJobs && groups.length > 1 && (
        <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--gray-500)', padding: '0 16px 8px', display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
          <span>Sort jobs by:</span>
          <button className="btn btn-outline btn-sm" onClick={() => sortGroupsBy('jobName')} title="Re-order the jobs by job name (stays until you change it)">Job name</button>
          <button className="btn btn-outline btn-sm" onClick={() => sortGroupsBy('jobId')} title="Re-order the jobs by job number (stays until you change it)">Job number</button>
        </div>
      )}

      {!isReadOnly && onAddFavorite && (
        <div style={{ textAlign: 'center', padding: '0 16px 10px' }}>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => setShowAddJob(true)}
          >
            + Add Job / Task
          </button>
        </div>
      )}

      <div className="tabular-scroll">
        <table className="tabular-table">
          <thead>
            <tr>
              <th className="tabular-job-header">
                <div>Job / Task</div>
                <div style={{ fontSize: '11px', fontWeight: 400, color: 'var(--gray-500)', marginTop: '2px' }}>
                  Default Rate Code: {defaultRateCodeName || 'Not specified.'}
                </div>
              </th>
              {dates.map((date) => {
                const { weekday, date: shortDate } = formatDayHeader(date);
                const weekend = isWeekend(date);
                return (
                  <th key={date} className={`tabular-date-header ${weekend ? 'weekend' : ''}`}>
                    <div>{weekday}</div>
                    <div className="tabular-date-sub">{shortDate}</div>
                  </th>
                );
              })}
              <th className="tabular-total-header">Total</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group, gIdx) => {
              const isGroupDragTarget = groupDragOverIdx === gIdx && groupDragIdx !== gIdx;
              return (
                <React.Fragment key={group.jobId}>
                  {group.tasks.map((job, tIdx) => {
                    const isFirst = tIdx === 0;
                    const isLast = tIdx === group.tasks.length - 1;
                    return (
                      <tr
                        key={job.key}
                        style={{
                          borderTop: isFirst
                            ? (isGroupDragTarget && groupDragIdx > gIdx ? '3px solid var(--primary)' : '2px solid var(--gray-200)')
                            : undefined,
                          borderBottom: isLast && isGroupDragTarget && groupDragIdx < gIdx ? '3px solid var(--primary)' : undefined,
                        }}
                      >
                        <td
                          className="tabular-job-cell"
                          draggable={!isReadOnly && isFirst}
                          onDragStart={!isReadOnly && isFirst ? (e) => handleGroupDragStart(e, gIdx) : undefined}
                          onDragEnd={!isReadOnly && isFirst ? handleGroupDragEnd : undefined}
                          onDragOver={groupDragIdx != null ? (e) => handleGroupDragOver(e, gIdx) : undefined}
                          onDragLeave={groupDragIdx != null ? handleGroupDragLeave : undefined}
                          onDrop={groupDragIdx != null ? (e) => handleGroupDrop(e, gIdx) : undefined}
                          style={{ cursor: !isReadOnly && isFirst ? 'grab' : undefined, verticalAlign: 'top' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                            {!isReadOnly && (
                              isFirst ? (
                                <span title="Drag to reorder job" style={{ color: 'var(--gray-300)', fontSize: '14px', lineHeight: '1', cursor: 'grab', userSelect: 'none', flexShrink: 0, paddingTop: '2px' }}>&#x2807;</span>
                              ) : (
                                <span style={{ width: '14px', flexShrink: 0 }} />
                              )
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {isFirst && (
                                <div className="tabular-job-name">{group.jobName} ({group.jobId})</div>
                              )}
                              <div className="tabular-task-name" style={{ paddingLeft: '10px' }}>{job.jobTaskId} – {job.taskName}</div>
                              {job.rateCodeId && defaultRateCodeId && job.rateCodeId !== defaultRateCodeId && (
                                <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px', paddingLeft: '10px' }}>
                                  Different Rate: {job.rateCodeName || job.rateCodeId}
                                  {job.rate != null ? ` (${formatRate(job.rate)})` : ''}
                                </div>
                              )}
                            </div>
                            {!isReadOnly && onDeleteJob && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteJob(job.jobId, job.jobTaskId);
                                }}
                                className="tabular-delete-btn"
                                title="Delete this task line"
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        </td>
                        {dates.map((date) => {
                          const hrs = byDateJob[date]?.[job.key] || 0;
                          const weekend = isWeekend(date);
                          const { weekday: cellWeekday, date: cellShortDate } = formatDayHeader(date);
                          const canDrag = !isReadOnly && hrs > 0;
                          const isCellDragTarget = dragOver && dragOver.jobKey === job.key && dragOver.date === date && !(dragSource?.jobKey === job.key && dragSource?.date === date);
                          const descriptions = descByDateJob[date]?.[job.key];
                          const hasDesc = descriptions && descriptions.length > 0;
                          const missingDesc = hrs > 0 && !hasDesc;
                          const tooltip = missingDesc ? 'Missing time entry description' : (hasDesc ? descriptions.join('\n') : undefined);
                          // Holiday job on a non-holiday day (and nothing charged): N/A, not enterable.
                          if (hrs === 0 && isHolidayNA(job.jobId, date)) {
                            return (
                              <td key={date} className={`tabular-cell ${weekend ? 'weekend' : ''}`}
                                style={{ background: 'var(--gray-100)', color: 'var(--gray-400)', fontSize: '10px', cursor: 'not-allowed' }}>
                                N/A
                              </td>
                            );
                          }
                          return (
                            <td
                              key={date}
                              className={`tabular-cell ${weekend ? 'weekend' : ''} ${hrs > 0 ? 'has-hours' : ''}`}
                              draggable={canDrag}
                              onDragStart={canDrag ? (e) => handleCellDragStart(e, job.key, date) : undefined}
                              onDragEnd={canDrag ? handleCellDragEnd : undefined}
                              onDragOver={dragSource ? (e) => handleCellDragOver(e, job.key, date) : undefined}
                              onDragLeave={dragSource ? handleCellDragLeave : undefined}
                              onDrop={dragSource ? (e) => handleCellDrop(e, job.key, date) : undefined}
                              onClick={() => onCellClick && onCellClick(job.jobId, job.jobTaskId, date)}
                              onContextMenu={(e) => handleCellRightClick(e, job, date)}
                              title={tooltip}
                              style={{
                                cursor: canDrag ? 'grab' : undefined,
                                outline: isCellDragTarget ? '2px solid var(--primary)' : undefined,
                                background: isCellDragTarget ? 'var(--primary-light)' : missingDesc ? '#fef9c3' : undefined,
                              }}
                            >
                              {hrs > 0 ? hrs.toFixed(2) : (
                                // Ghost date in empty cells so the day is readable
                                // even when the header has scrolled off screen —
                                // weekday on top, m/d underneath.
                                <span style={{ color: 'var(--gray-300)', fontSize: '10px', whiteSpace: 'nowrap', lineHeight: 1.15, display: 'inline-block' }}>
                                  {cellWeekday}<br />{cellShortDate}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="tabular-row-total">
                          {jobTotals[job.key] > 0 ? jobTotals[job.key].toFixed(2) : ''}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="tabular-totals-row">
              <td className="tabular-job-cell"><strong>Daily Total</strong></td>
              {dates.map((date) => {
                const total = dateTotals[date] || 0;
                const weekend = isWeekend(date);
                return (
                  <td key={date} className={`tabular-cell total ${weekend ? 'weekend' : ''} ${total > 24 ? 'over-limit' : ''}`}
                    style={total > 24 ? { background: '#fecaca', color: '#dc2626', fontWeight: 700 } : undefined}
                  >
                    <strong>{total > 0 ? total.toFixed(2) : ''}</strong>
                  </td>
                );
              })}
              <td className="tabular-row-total">
                <strong>{grandTotal.toFixed(2)}</strong>
              </td>
            </tr>
            <tr className="tabular-variance-row">
              <td className="tabular-job-cell">Over / Under</td>
              {dates.map((date) => {
                const actual = dateTotals[date] || 0;
                const expected = expectedHours(date);
                const variance = actual - expected;
                const weekend = isWeekend(date);
                return (
                  <td
                    key={date}
                    className={`tabular-cell variance ${weekend ? 'weekend' : ''}`}
                    style={{
                      color: actual > 24 ? '#dc2626'
                        : variance > 0 ? '#059669'
                        : variance < 0 ? '#dc2626'
                        : 'var(--gray-400)',
                    }}
                  >
                    {(actual > 0 || !weekend) ? (
                      <>{variance >= 0 ? '+' : ''}{variance.toFixed(2)}</>
                    ) : ''}
                  </td>
                );
              })}
              <td
                className="tabular-row-total variance"
                style={{
                  color: totalVariance > 0 ? '#059669'
                    : totalVariance < 0 ? '#dc2626'
                    : 'var(--gray-400)',
                }}
              >
                {totalVariance >= 0 ? '+' : ''}{totalVariance.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {showAddJob && (
        // AddJobModal renders itself into document.body via a portal.
        <AddJobModal
          customers={customers}
          multiSelect
          onAdd={(jobId, jobTaskIds) => {
            onAddFavorite(jobId, jobTaskIds);
            setShowAddJob(false);
          }}
          onClose={() => setShowAddJob(false)}
          existingFavorites={favorites}
        />
      )}

      {editPopup && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '90vw', maxWidth: '420px', background: 'white', borderRadius: '8px', overflow: 'hidden' }}
          >
            <div style={{ padding: '16px', borderBottom: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: 600 }}>Edit Time Entry</span>
                <IconButton icon="close" title="Close" onClick={handleEditSave} />
              </div>
            </div>
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '13px', color: 'var(--gray-600)' }}>
                <strong>{editPopup.job.jobName} ({editPopup.job.jobId})</strong>
                <span style={{ marginLeft: '8px', color: 'var(--gray-500)' }}>
                  {editPopup.job.jobTaskId} &ndash; {editPopup.job.taskName}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                {displayDate(editPopup.date, { weekday: true })}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Hours</label>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  max="24"
                  value={editHours}
                  onChange={(e) => setEditHours(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(); }}
                  style={{ width: '100%', padding: '8px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '14px' }}
                  autoFocus
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Description</label>
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditSave(); } }}
                  maxLength={250}
                  rows={3}
                  style={{ width: '100%', padding: '8px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }}
                />
              </div>
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--gray-200)', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setEditPopup(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditSave} disabled={editSaving}>
                {editSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </WideSection>
  );
}
